import { showUserAlert } from "../utils/userErrorMessage";
// pages/ReservationGroupEdit.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import TimeSelect from "../components/ui/time-select";

const SMS_SENDING_PAUSED = true;

async function findDriverPhone({ supabase, company, human_name }) {
  if (!human_name) return "";

  const { data, error } = await supabase
    .from("profiles")
    .select("phone")
    .eq("company", company)
    .eq("role", "driver")
    .eq("driver_name", human_name)
    .maybeSingle();

  if (error) {
    console.error("기사 전화번호 조회 실패:", error.message);
    return "";
  }
  return (data?.phone || "").trim();
}

function shouldSendSMS(prevRow, nextRow) {
  const prevPhone = (prevRow?.phone || "").trim();
  const nextPhone = (nextRow?.phone || "").trim();
  const prevDriver = (prevRow?.human_name || "").trim();
  const nextDriver = (nextRow?.human_name || "").trim();
  const prevCar = (prevRow?.car_number || "").trim();
  const nextCar = (nextRow?.car_number || "").trim();

  // 기사 미지정 -> 지정됨 (전화 생김)
  if (!prevPhone && nextPhone) return true;

  // 기사 변경
  if (prevDriver && nextDriver && prevDriver !== nextDriver) return true;

  // 차량 변경
  if (prevCar && nextCar && prevCar !== nextCar) return true;

  return false;
}


export default function ReservationGroupEdit() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const company = localStorage.getItem("email") || "";

  const [summary, setSummary] = useState(null); // reservation_summary single
  const [rows, setRows] = useState([]);         // reservations[]
  const [removedIds, setRemovedIds] = useState([]); // 🔥 삭제 예정 행 id 모음
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkApplyFromDate, setBulkApplyFromDate] = useState("");

  

  // 초기 로드
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data: s, error: sErr } = await supabase
          .from("reservation_summary")
          .select("*")
          .eq("group_id", groupId)
          .eq("company", company)
          .single();
        if (sErr) throw sErr;

        const { data: r, error: rErr } = await supabase
          .from("reservations")
          .select("*")
          .eq("reservation_group_id", groupId)
          .eq("company", company)
          .order("date", { ascending: true })
          .order("time", { ascending: true });

        if (rErr) throw rErr;

        setSummary(s);
        setRows(r || []);
        setBulkApplyFromDate(s?.start_date || "");
      } catch (e) {
        showUserAlert("불러오기 실패: " + e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [company, groupId]);

  const handleDeleteGroup = async () => {
    if (!summary) return;
    const ok = window.confirm(
        `정말 삭제할까요?\n\n- 그룹ID: ${summary.group_id}\n- 예약(하위 행)과 요약(상위 행)까지 모두 삭제됩니다.`
    );
    if (!ok) return;

    setDeleting(true);
    try {
        // 🔥 RPC 호출 (트랜잭션 처리)
        const { error } = await supabase.rpc("delete_reservation_group", { gid: summary.group_id });
        if (error) throw error;

        showUserAlert("그룹 전체가 삭제되었습니다.");
        navigate(-1);
    } catch (e) {
        showUserAlert("삭제 실패: " + e.message);
    } finally {
        setDeleting(false);
    }
  };

  const handleSummaryChange = (field, value) => {
    setSummary((prev) => ({ ...prev, [field]: value }));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // 시작/종료일 및 박수 동기화(간단 버전)
  // - days 변경 → end_date = start_date + days
  // - start_date 변경 → end_date = start_date + days
  // - end_date 직접 바꾸면 days 재계산
  const updateEndByDays = (start, days) => {
    if (!start || days === "" || days == null) return "";
    const base = new Date(start);
    base.setDate(base.getDate() + parseInt(days, 10));
    return base.toISOString().split("T")[0];
  };

  const diffDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    const d = Math.round((e - s) / (1000 * 60 * 60 * 24));
    return d;
  };

  // 날짜 YYYY-MM-DD 문자열 헬퍼
    const ymd = (d) => (new Date(d)).toISOString().split("T")[0];

    // start_date 기준으로 i일 더한 날짜
    const addDays = (dateStr, i) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + i);
    return ymd(d);
    };

    // 요약값을 기본으로 한 “신규 행” 템플릿
    // 글로벌 인덱스 k를 받아서, 날짜 = start_date + floor(k / 차량대수)
    const makeNewRow = (k, summarySource = summary) => {
      const perDay = parseInt(summarySource.number_of_car ?? 1, 10) || 1;
      const dayOffset = Math.floor(k / perDay);
      return {
        id: undefined,
        reservation_group_id: summarySource.group_id,
        date: addDays(summarySource.start_date, dayOffset),
        time: summarySource.time || null,
        arrival_time: summarySource.arrival_time || null,
        round_trip_time: summarySource.round_trip_time || null,
        start: summarySource.start || "",
        transit: summarySource.transit || "",
        destination: summarySource.destination || "",
        user: summarySource.user || "",
        money: k < perDay ? summarySource.money || 0 : 0,
        driver_money: summarySource.driver_money || 0,
        one_round: "왕복",
        number_of_car: 1, // 행은 '차량 1대'를 의미
        car_number: "",
        human_name: "",
        phone: "",
        seat: summarySource.car_type || null,
        reservation_type: summarySource.reservation_type,
        company: summarySource.company,
        
      };
    };

    // 🔥 기간 동기화: 행 개수(길이)와 날짜를 start_date부터 연속으로 맞춤
    const syncRowsWithPeriod = (summarySource = summary, opts = { askBeforeRemove: true }) => {
        if (!summarySource?.start_date) return;
        const days = (parseInt(summarySource.days ?? 0, 10) || 0) + 1; // 날짜 개수
        const perDay = parseInt(summarySource.number_of_car ?? 1, 10) || 1; // 날짜별 차량대수
        const targetCount = days * perDay; // 총 행 수
        if (targetCount <= 0) return;

        // 일단 날짜만 start_date+i로 재배치
        const sorted = [...rows].sort((a, b) =>
          (a.date || "").localeCompare(b.date || "") || (a.time || "").localeCompare(b.time || "")
        );
        const reassigned = sorted.map((r, i) => {
          const dayOffset = Math.floor(i / perDay);
          return { ...r, date: addDays(summarySource.start_date, dayOffset) };
        });

        if (reassigned.length === targetCount) {
            setRows(reassigned);
            return;
        }

        if (reassigned.length > targetCount) {
            // 🔪 줄여야 함 → 뒤쪽 행 잘라냄
            const toKeep = reassigned.slice(0, targetCount);
            const toRemove = reassigned.slice(targetCount);

            if (!opts.askBeforeRemove || window.confirm(`기간 변경으로 ${toRemove.length}개 행을 삭제합니다. 계속할까요?`)) {
            // DB에 이미 있는 행 id만 모아 삭제 예약
            const willRemoveIds = toRemove.filter(r => !!r.id).map(r => r.id);
            setRemovedIds(prev => [...prev, ...willRemoveIds]);
            setRows(toKeep);
            }
        } else {
            // ➕ 늘려야 함 → 부족분 생성해서 뒤에 채움
            const need = targetCount - reassigned.length;            
            const extra = Array.from({ length: need }, (_, k) =>
              makeNewRow(reassigned.length + k, summarySource) // 글로벌 인덱스 기준으로 날짜 계산
            );
            setRows([...reassigned, ...extra]);
        }
    };

  // 상단 값 일부를 일괄 적용
  const applyBulk = () => {
    if (!summary) return;
    if (!bulkApplyFromDate) {
      showUserAlert("상단값을 일괄 적용할 시작일을 선택해 주세요.");
      return;
    }
    const fields = ["time", "arrival_time", "round_trip_time", "start", "transit", "destination", "user", "seat"]; // 필요시 추가/수정
    const targetRows = rows.filter((r) => (r.date || "") >= bulkApplyFromDate);
    const firstTargetDate = targetRows
      .map((r) => r.date)
      .filter(Boolean)
      .sort()[0];
    const perDay = parseInt(summary.number_of_car ?? 1, 10) || 1;
    const firstTargetDateRowIndexes = new Set();
    rows.forEach((r, index) => {
      if (r.date === firstTargetDate && firstTargetDateRowIndexes.size < perDay) {
        firstTargetDateRowIndexes.add(index);
      }
    });
    const next = rows.map((r, index) => {
      if ((r.date || "") < bulkApplyFromDate) return r;
      const copy = { ...r };
      fields.forEach((f) => {
        if (summary[f] !== undefined) copy[f] = summary[f] || null;
      });
      if (summary.money != null) {
        copy.money = firstTargetDateRowIndexes.has(index) ? summary.money : 0;
      }
      if (summary.driver_money != null) copy.driver_money = summary.driver_money;
      return copy;
    });
    setRows(next);
  };

  const handleSave = async () => {
    if (!summary) return;
    setSaving(true);

    try {

      // handleSave 맨 앞쪽(try 안, 저장 로직 전에) 추가
      const { data: beforeRows, error: beforeErr } = await supabase
        .from("reservations")
        .select("id, driver_id")
        .eq("reservation_group_id", summary.group_id)
        .eq("company", company);

      if (beforeErr) throw beforeErr;

      // id -> driver_id 맵
      const beforeDriverMap = new Map(
        (beforeRows || []).map(r => [r.id, r.driver_id])
      );

      // ✅ 0) 기존 reservations(이 그룹) 상태를 먼저 읽어서 "이전값" 확보
      const { data: prevRows, error: prevErr } = await supabase
        .from("reservations")
        .select("id, date, start, destination, human_name, car_number, phone")
        .eq("reservation_group_id", summary.group_id)
        .eq("company", company);

      if (prevErr) throw prevErr;

      const prevMap = new Map((prevRows || []).map((r) => [r.id, r]));

      // 1) 요약 테이블 업데이트 (원본 유지)
      const payloadSummary = {
        start_date: summary.start_date,
        end_date: summary.end_date,
        days: summary.days,
        time: summary.time || null,
        arrival_time: summary.arrival_time || null,
        round_trip_time: summary.round_trip_time || null,
        number_of_car: summary.number_of_car,
        user: summary.user,
        reservation_type: summary.reservation_type,
        car_type: summary.car_type,
        start: summary.start,
        destination: summary.destination,
        transit: summary.transit,
        money: summary.money || 0,
        driver_money: summary.driver_money || 0,
      };

      // ✅ 2-B-0) rows를 저장하기 전에 phone 자동 채움
      // - 배차처리에서 human_name만 들어가고 phone이 비어있을 수 있음
      const reservationCompany = summary.company || company;

      const rowsWithPhone = await Promise.all(
        rows.map(async (r) => {
          const driverName = (r.human_name || "").trim();
          const phoneNow = (r.phone || "").trim();

          // human_name이 있고 phone이 비어있으면 profiles에서 찾아 채움
          if (driverName && !phoneNow) {
            const p = await findDriverPhone({ supabase, company: reservationCompany, human_name: driverName });
            return { ...r, phone: p || "" };
          }
          return r;
        })
      );

      // ✅ "문자 보내야 하는 rows" 미리 계산 (저장 후 발송)
      // - id 있는 건 prevMap과 비교
      // - id 없는 insert는 driver 지정되어 phone 있으면 발송 대상으로
      const smsTargets = [];
      for (const r of rowsWithPhone) {
        const nextRow = {
          id: r.id,
          date: r.date,
          start: r.start,
          destination: r.destination,
          human_name: r.human_name,
          car_number: r.car_number,
          phone: r.phone,
        };

        if (!r.id) {
          // 새로 추가된 행인데 기사/전화가 있으면 발송
          if ((nextRow.phone || "").trim()) smsTargets.push(nextRow);
        } else {
          const prevRow = prevMap.get(r.id);
          if (shouldSendSMS(prevRow, nextRow)) smsTargets.push(nextRow);
        }
      }

      // 2-B) rows에서 id 없는 건 insert, 있는 건 update (네 코드 유지 + rowsWithPhone 사용)
      const toInsert = rowsWithPhone
        .filter((r) => !r.id)
        .map((r) => ({
          reservation_group_id: summary.group_id,
          date: r.date,
          time: r.time || null,
          arrival_time: r.arrival_time || null,
          round_trip_time: r.one_round === "왕복" ? r.round_trip_time || null : null,
          start: r.start,
          transit: r.transit,
          destination: r.destination,
          user: r.user,
          money: r.money || 0,
          driver_money: r.driver_money || 0,
          one_round: r.one_round || "왕복",
          number_of_car: r.number_of_car || 1,
          car_number: r.car_number || "",
          human_name: r.human_name || "",
          phone: r.phone || "", // ✅ 자동 채워진 phone
          seat: r.seat || summary.car_type || null,
          reservation_type: summary.reservation_type,
          company: summary.company,          
          driver_id: r.driver_id || null,
        }));

      const toUpdate = rowsWithPhone
        .filter((r) => !!r.id)
        .map((r) => ({
          id: r.id,
          data: {
            date: r.date,
            time: r.time || null,
            arrival_time: r.arrival_time || null,
            round_trip_time: r.one_round === "왕복" ? r.round_trip_time || null : null,
            start: r.start,
            transit: r.transit,
            destination: r.destination,
            user: r.user,
            money: r.money || 0,
            driver_money: r.driver_money || 0,
            one_round: r.one_round || "왕복",
            number_of_car: r.number_of_car || 1,
            car_number: r.car_number || "",
            human_name: r.human_name || "",
            phone: r.phone || "", // ✅ 자동 채워진 phone
            seat: r.seat || summary.car_type || null,
            driver_id: r.driver_id || null,
          },
        }));

      const { error: saveError } = await supabase.rpc("save_reservation_group", {
        p_group: summary.group_id, p_summary: payloadSummary,
        p_inserts: toInsert, p_updates: toUpdate, p_deleted_ids: removedIds.map(String),
      });
      if (saveError) throw saveError;

      // ✅ 저장 완료 후, driver_id가 새로 생기거나 변경된 row만 추출
      const changedRows = rowsWithPhone
        .filter(r => !!r.driver_id) // driver_id 있는 것만
        .filter(r => {
          if (!r.id) return true; // 신규 insert는 driver_id 있으면 발송
          const before = beforeDriverMap.get(r.id) || null;
          return before !== r.driver_id; // 변경 감지
        });

      if (process.env.NODE_ENV !== "production") console.log("rows snapshot:", rows.map(r => ({
        id: r.id,
        driver_id: r.driver_id,
        human_name: r.human_name,
        phone: r.phone,
        date: r.date
      })));

      if (process.env.NODE_ENV !== "production") console.log("changedRows length:", changedRows.length, changedRows);


      // ✅ 3) 저장 성공 후, 문자 발송 (솔라피 Edge Function 호출)
      // - send-reservation-sms 라는 함수로 만들어놨다는 가정
      // - 실패해도 저장 자체는 성공으로 처리하고, 콘솔/알림만
      if (SMS_SENDING_PAUSED && smsTargets.length > 0) {
        console.warn("SMS sending is paused. Skipped targets:", smsTargets.length);
      } else if (smsTargets.length > 0) {
        const sends = smsTargets
          .filter((t) => (t.phone || "").trim())
          .map((t) =>
            supabase.functions.invoke("send-reservation-sms", {
              body: {
                to: t.phone,
                human_name: t.human_name,
                date: t.date,
                start: t.start,
                destination: t.destination,
              },
            })
          );

        const sendResults = await Promise.all(sends);
        const sendFail = sendResults.find((r) => r.error || r.data?.error);
        if (sendFail) {
          console.warn("문자 발송 일부 실패:", sendFail.error || sendFail.data?.error);
          // 원하면 alert 띄워도 됨
          // alert("저장은 완료되었으나, 문자 발송 일부가 실패했습니다. 로그를 확인하세요.");
        }
      }
      // ✅ 기사 전화번호는 profiles에서 driver_id로 조회(정확)
      if (SMS_SENDING_PAUSED && changedRows.length > 0) {
        console.warn("SMS sending is paused. Skipped changed rows:", changedRows.length);
      } else for (const r of changedRows) {
        // 1) driver_id로 기사 정보 조회
        const { data: driver, error: dErr } = await supabase
          .from("profiles")
          .select("driver_name, phone")
          .eq("id", r.driver_id)
          .eq("company", company)
          .maybeSingle();

        if (dErr) {
          console.warn("driver lookup fail:", dErr.message);
          continue;
        }
        if (!driver?.phone) {
          console.warn("no driver phone for:", r.driver_id);
          continue;
        }

        // 2) Edge Function 호출 (트리거 payload 형태 유지)
        const { error: smsErr } = await supabase.functions.invoke("send-reservation-sms", {
          body: {
            record: {
              phone: driver.phone,                // 010...
              human_name: driver.driver_name || r.human_name || "기사",
              date: r.date,
              start: r.start,
              destination: r.destination,
            },
          },
        });

        if (smsErr) {
          console.warn("sms send fail:", smsErr.message);
        }
      }


      showUserAlert("예약이 저장되었습니다.");
      setRemovedIds([]);
      navigate(-1);
    } catch (e) {
      showUserAlert("저장 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };


  if (loading) return <div className="p-4">불러오는 중...</div>;
  if (!summary) return <div className="p-4">데이터가 없습니다.</div>;

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-semibold">예약 그룹 수정</h2>

      {/* 요약 카드 */}
      <Card>
        <CardContent className="space-y-3 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-semibold">구분</label>
              <select
                value={summary.reservation_type || ""}
                onChange={(e) => handleSummaryChange("reservation_type", e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="일반">일반</option>
                <option value="학단">학단</option>
                <option value="투어">투어</option>
                <option value="통근">통근</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold">차종</label>
              <select
                value={summary.car_type || ""}
                onChange={(e) => handleSummaryChange("car_type", e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">선택</option>
                <option>대형</option>
                <option>중형</option>
                <option>우등</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold">차량대수</label>
              <Input
                type="number"
                value={summary.number_of_car || 1}
                onChange={(e) => {
                  const num = parseInt(e.target.value || 1, 10);
                  const nextSummary = { ...summary, number_of_car: num };
                  setSummary(nextSummary);
                  syncRowsWithPeriod(nextSummary); // 🔥 차량대수 변경 시 바로 동기화
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold">시작일</label>
              <Input
                type="date"
                value={summary.start_date || ""}
                onChange={(e) => {
                    const start_date = e.target.value;
                    const end_date = updateEndByDays(start_date, summary.days ?? 0);
                    const nextSummary = { ...summary, start_date, end_date };
                    setSummary(nextSummary);
                    syncRowsWithPeriod(nextSummary); // 🔥 기간 동기화
                }}
                />
            </div>
            <div>
              <label className="block text-sm font-semibold">박수</label>
              <Input
                type="number"
                value={summary.days ?? 0}
                onChange={(e) => {
                    const days = parseInt(e.target.value || 0, 10);
                    const end_date = updateEndByDays(summary.start_date, days);
                    const nextSummary = { ...summary, days, end_date };
                    setSummary(nextSummary);
                    syncRowsWithPeriod(nextSummary); // 🔥 기간 동기화
                }}
                />
            </div>
            <div>
              <label className="block text-sm font-semibold">종료일</label>
              <Input
                type="date"
                value={summary.end_date || ""}
                onChange={(e) => {
                    const end_date = e.target.value;
                    const days = diffDays(summary.start_date, end_date);
                    const nextSummary = { ...summary, end_date, days };
                    setSummary(nextSummary);
                    syncRowsWithPeriod(nextSummary); // 🔥 기간 동기화
                }}
                />
            </div>

            <div>
              <label className="block text-sm font-semibold">출발시간</label>
              <TimeSelect value={summary.time || ""} onChange={(value) => handleSummaryChange("time", value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold">도착 예상 시간</label>
              <TimeSelect value={summary.arrival_time || ""} onChange={(value) => handleSummaryChange("arrival_time", value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold">회차 시간(왕복)</label>
              <TimeSelect value={summary.round_trip_time || ""} onChange={(value) => handleSummaryChange("round_trip_time", value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold">사용자</label>
              <Input
                value={summary.user || ""}
                onChange={(e) => handleSummaryChange("user", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold">받을 금액(기본)</label>
              <Input
                type="number"
                value={summary.money || 0}
                onChange={(e) => handleSummaryChange("money", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold">기사 금액(기본)</label>
              <Input
                type="number"
                value={summary.driver_money || 0}
                onChange={(e) => handleSummaryChange("driver_money", e.target.value)}
              />
            </div>

            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-semibold">출발지</label>
                <Input
                  value={summary.start || ""}
                  onChange={(e) => handleSummaryChange("start", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold">경유지</label>
                <Input
                  value={summary.transit || ""}
                  onChange={(e) => handleSummaryChange("transit", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold">행선지</label>
                <Input
                  value={summary.destination || ""}
                  onChange={(e) => handleSummaryChange("destination", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            {/* <Button variant="outline" onClick={reassignDates}>날짜 재배치</Button> */}
            <Button variant="outline" onClick={() => syncRowsWithPeriod()}>기간과 행 동기화</Button>
            <Input
              type="date"
              value={bulkApplyFromDate}
              onChange={(e) => setBulkApplyFromDate(e.target.value)}
              className="w-40"
              title="이 날짜부터 상단값을 일괄 적용합니다."
            />
            <Button variant="outline" onClick={applyBulk}>해당일부터 상단값 일괄 적용</Button>
          </div>
        </CardContent>
      </Card>

      {/* 개별 예약 목록 */}
      <Card>
        <CardContent className="bg-white">
          <div className="overflow-x-auto">
            <table className="table-auto w-full border border-gray-300 text-sm whitespace-nowrap">
              <thead className="bg-blue-500 text-white">
                <tr>
                  {["순번","날짜","시간","도착 예상","회차 시간","출발지","경유지","행선지","사용자","차량번호","기사명","연락처","편도/왕복","받을 금액","기사 금액"].map(h => (
                    <th key={h} className="border p-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td className="border p-1 text-center">{idx + 1}</td>
                    <td className="border p-1">
                      <Input
                        type="date"
                        value={r.date || ""}
                        onChange={(e) => handleRowChange(idx, "date", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <TimeSelect value={r.time || ""} onChange={(value) => handleRowChange(idx, "time", value)} compact />
                    </td>
                    <td className="border p-1">
                      <TimeSelect value={r.arrival_time || ""} onChange={(value) => handleRowChange(idx, "arrival_time", value)} compact />
                    </td>
                    <td className="border p-1">
                      {(r.one_round || "왕복") === "왕복" ? (
                        <TimeSelect value={r.round_trip_time || ""} onChange={(value) => handleRowChange(idx, "round_trip_time", value)} compact />
                      ) : (
                        <span className="block text-center text-gray-400">-</span>
                      )}
                    </td>
                    <td className="border p-1">
                      <Input
                        value={r.start || ""}
                        onChange={(e) => handleRowChange(idx, "start", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        value={r.transit || ""}
                        onChange={(e) => handleRowChange(idx, "transit", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        value={r.destination || ""}
                        onChange={(e) => handleRowChange(idx, "destination", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        value={r.user || ""}
                        onChange={(e) => handleRowChange(idx, "user", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        value={r.car_number || ""}
                        onChange={(e) => handleRowChange(idx, "car_number", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        value={r.human_name || ""}
                        onChange={(e) => handleRowChange(idx, "human_name", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        value={r.phone || ""}
                        onChange={(e) => handleRowChange(idx, "phone", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <select
                        className="border rounded px-2 py-1"
                        value={r.one_round || "왕복"}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRows((prev) => {
                            const next = [...prev];
                            next[idx] = {
                              ...next[idx],
                              one_round: value,
                              round_trip_time: value === "왕복" ? next[idx].round_trip_time : null,
                            };
                            return next;
                          });
                        }}
                      >
                        <option>편도</option>
                        <option>왕복</option>
                      </select>
                    </td>
                    <td className="border p-1">
                      <Input
                        type="number"
                        value={r.money || 0}
                        onChange={(e) => handleRowChange(idx, "money", e.target.value)}
                      />
                    </td>
                    <td className="border p-1">
                      <Input
                        type="number"
                        value={r.driver_money || 0}
                        onChange={(e) => handleRowChange(idx, "driver_money", e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-right mt-3 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => navigate(-1)}>취소</Button>
            <Button
                onClick={handleDeleteGroup}
                disabled={deleting || saving}
                className="bg-red-600 text-white hover:bg-red-700"
            >
                {deleting ? "삭제중..." : "삭제"}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "저장중..." : "저장"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
